// AuthContext - Global authentication state management
// Ensures user-type API is only called once and shared across all components
//
// ALIGNMENT WITH ANGULAR:
// - Angular uses loginService with user_id caching (login.service.js line 48-86)
// - Angular caches after first call: if(user_id == null) { fetch } else { return cached }
// - Our approach is BETTER: Angular still allows multiple calls from different components
// - We use React Context to ensure SINGLE call app-wide, then share result
// - Both implementations cache user_id to avoid redundant API calls
// - Both handle authentication failures gracefully without caching errors

import React, { createContext, useContext, useState, useEffect } from "react";
import sharedListApi from "../services/sharedListApi";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChecked, setHasChecked] = useState(false);

  useEffect(() => {
    // Only fetch once on app initialization
    if (hasChecked) return;

    const checkAuth = async () => {
      try {
        console.log("[AuthContext] Checking authentication (once)");
        setIsLoading(true);
        const id = await sharedListApi.getUserId();
        setUserId(id);
        setError(null);
        setHasChecked(true);
        console.log("[AuthContext] User ID cached:", id || "not authenticated");
      } catch (err) {
        console.error("[AuthContext] Error checking auth:", err);
        setError(err);
        setUserId(null);
        setHasChecked(true);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [hasChecked]);

  // Provide a method to refresh auth if needed
  const refreshAuth = async () => {
    console.log("[AuthContext] Refreshing authentication");
    setIsLoading(true);
    try {
      // Clear the cached user_id in sharedListApi to force a fresh check
      sharedListApi.user_id = null;
      const id = await sharedListApi.getUserId();
      setUserId(id);
      setError(null);
    } catch (err) {
      console.error("[AuthContext] Error refreshing auth:", err);
      setError(err);
      setUserId(null);
    } finally {
      setIsLoading(false);
    }
  };

  const value = {
    userId,
    isLoading,
    error,
    isAuthenticated: !!userId,
    refreshAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
